import { Link } from 'react-router-dom';
import LegalPage, { LegalSection } from '../../components/legal/LegalPage';

const ulCls = 'list-disc space-y-1.5 pl-5';

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      lastUpdated="May 22, 2026"
      intro={
        <p>
          This Privacy Policy explains how Calendyz (&ldquo;Calendyz&rdquo;, &ldquo;we&rdquo;,
          &ldquo;us&rdquo;) collects, uses, and protects your personal information when you use the
          Calendyz website and booking platform at{' '}
          <span className="text-fg-secondary font-medium">calendyz.com</span>.
        </p>
      }
    >
      <LegalSection heading="1. Who we are">
        <p>
          Calendyz is an online platform that connects customers with beauty and grooming salons.
          Customers browse a directory of salons, view profiles, and book appointments. Salon owners
          manage their listings and appointments through an admin dashboard.
        </p>
        <p>
          For any privacy-related question, contact us at{' '}
          <a href="mailto:support@calendyz.com" className="text-accent hover:underline">
            support@calendyz.com
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="2. Information we collect">
        <p>We collect only the information needed to operate the booking service:</p>
        <ul className={ulCls}>
          <li>
            <strong>Account information.</strong> When you register, we collect your name, email
            address, phone number, and a password (stored only as a secure cryptographic hash).
          </li>
          <li>
            <strong>Social sign-in information.</strong> If you choose to sign in with Google, we
            receive a verified token from Google and use it to obtain your name, email address, and
            a unique provider account identifier. We do <em>not</em> receive your social-network
            password, and we do not post anything to your social accounts.
          </li>
          <li>
            <strong>Booking information.</strong> The salons, services, dates, and times of
            appointments you make, and the contact details attached to those bookings.
          </li>
          <li>
            <strong>Reviews.</strong> Ratings and written comments you submit about salons you have
            visited, displayed publicly alongside your first name.
          </li>
          <li>
            <strong>Technical information.</strong> Authentication cookies, and limited diagnostic
            data (such as error reports) used to keep the service secure and reliable.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="3. How we use your information">
        <ul className={ulCls}>
          <li>To create and manage your account and authenticate you when you sign in.</li>
          <li>To process bookings and share the necessary details with the salon you book.</li>
          <li>To verify your email address and, where applicable, reset your password.</li>
          <li>To publish reviews you choose to submit.</li>
          <li>To send service-related messages (booking confirmations, verification codes).</li>
          <li>To secure the platform, prevent abuse, and diagnose technical problems.</li>
        </ul>
        <p>
          We do not sell your personal information, and we do not use it for third-party
          advertising.
        </p>
      </LegalSection>

      <LegalSection heading="4. How we share your information">
        <p>We share information only in these limited cases:</p>
        <ul className={ulCls}>
          <li>
            <strong>With salons.</strong> When you book an appointment, the relevant salon receives
            your name, phone number, and booking details so it can serve you.
          </li>
          <li>
            <strong>With service providers.</strong> We use trusted infrastructure providers to
            operate the platform — including an email delivery provider (for verification and
            notification emails), a network and security provider, and an error-monitoring service.
            They process data only on our behalf.
          </li>
          <li>
            <strong>For legal reasons.</strong> When required by law, or to protect the rights,
            safety, and security of Calendyz, our users, or the public.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. Google sign-in">
        <p>
          Signing in with Google is optional. When you use it, Google authenticates you and returns
          a verified token. We use that token solely to create or access your Calendyz account. Your
          use of Google is also governed by its own privacy policy (
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Google
          </a>
          ). Calendyz&rsquo;s use of information received from Google APIs adheres to the{' '}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
      </LegalSection>

      <LegalSection heading="6. Cookies">
        <p>
          We use a small number of strictly necessary cookies to keep you signed in. These
          authentication cookies are HttpOnly and cannot be read by scripts. We do not use
          advertising or third-party tracking cookies.
        </p>
      </LegalSection>

      <LegalSection heading="7. Data retention">
        <p>
          We keep your account information for as long as your account is active. Booking records
          are retained as needed to provide the service and meet legal or accounting obligations.
          When you delete your account, we delete or anonymize your personal information, except
          where we are required to retain it by law.
        </p>
      </LegalSection>

      <LegalSection heading="8. Your rights">
        <p>You may, at any time:</p>
        <ul className={ulCls}>
          <li>Access and update your profile information from your account settings.</li>
          <li>Request a copy of the personal data we hold about you.</li>
          <li>Request deletion of your account and associated personal data.</li>
          <li>Withdraw consent for optional processing.</li>
        </ul>
        <p>
          To exercise any of these rights, email{' '}
          <a href="mailto:support@calendyz.com" className="text-accent hover:underline">
            support@calendyz.com
          </a>
          . We will respond within a reasonable timeframe.
        </p>
      </LegalSection>

      <LegalSection heading="9. Security">
        <p>
          We protect your data with industry-standard measures: passwords are hashed, connections
          are encrypted in transit (HTTPS), authentication tokens are short-lived and stored in
          HttpOnly cookies, and access to systems is restricted. No method of transmission or
          storage is completely secure, but we work continuously to safeguard your information.
        </p>
      </LegalSection>

      <LegalSection heading="10. Children">
        <p>
          Calendyz is not directed to children under 16. We do not knowingly collect personal
          information from children. If you believe a child has provided us information, contact us
          and we will remove it.
        </p>
      </LegalSection>

      <LegalSection heading="11. Changes to this policy">
        <p>
          We may update this Privacy Policy from time to time. When we do, we will revise the
          &ldquo;Last updated&rdquo; date above. Significant changes will be communicated through
          the platform.
        </p>
      </LegalSection>

      <LegalSection heading="12. Contact us">
        <p>
          Questions about this policy or your data? Email{' '}
          <a href="mailto:support@calendyz.com" className="text-accent hover:underline">
            support@calendyz.com
          </a>
          . See also our{' '}
          <Link to="/terms" className="text-accent hover:underline">
            Terms of Service
          </Link>
          .
        </p>
      </LegalSection>
    </LegalPage>
  );
}
